import {
  signIn,
  signUp,
  signOut,
  confirmSignUp,
  resetPassword,
  confirmResetPassword,
  getCurrentUser,
  fetchAuthSession,
  signInWithRedirect,
  type SignInInput,
  type SignUpInput,
} from 'aws-amplify/auth';

export interface AuthUser {
  userId: string;
  username: string;
}

/**
 * Sign in with email and password.
 */
export async function login(email: string, password: string) {
  const input: SignInInput = {
    username: email,
    password,
  };
  const result = await signIn(input);
  return result;
}

/**
 * Sign in with Google (redirects to Google's consent screen).
 * After authentication, the user is redirected back to the app's
 * configured OAuth callback URL.
 */
export async function loginWithGoogle() {
  await signInWithRedirect({ provider: 'Google' });
}

/**
 * Sign up a new user with email, password, and display name.
 */
export async function register(email: string, password: string, displayName: string) {
  const input: SignUpInput = {
    username: email,
    password,
    options: {
      userAttributes: {
        email,
        name: displayName,
      },
    },
  };
  const result = await signUp(input);
  return result;
}

/**
 * Confirm sign up with verification code.
 */
export async function confirmRegistration(email: string, code: string) {
  const result = await confirmSignUp({
    username: email,
    confirmationCode: code,
  });
  return result;
}

/**
 * Sign out the current user.
 */
export async function logout() {
  await signOut();
}

/**
 * Request a password reset code.
 */
export async function requestPasswordReset(email: string) {
  const result = await resetPassword({ username: email });
  return result;
}

/**
 * Confirm password reset with code and new password.
 */
export async function confirmPasswordReset(
  email: string,
  code: string,
  newPassword: string,
) {
  const result = await confirmResetPassword({
    username: email,
    confirmationCode: code,
    newPassword,
  });
  return result;
}

/**
 * Get the currently authenticated user, or null if not authenticated.
 */
export async function getAuthUser(): Promise<AuthUser | null> {
  try {
    const user = await getCurrentUser();
    return {
      userId: user.userId,
      username: user.username,
    };
  } catch {
    return null;
  }
}

/**
 * Check if the user has a valid session.
 */
export async function isAuthenticated(): Promise<boolean> {
  try {
    const session = await fetchAuthSession();
    //console.log(session.tokens);
    return !!session.tokens?.idToken;
  } catch {
    return false;
  }
}
